/**
 * VRMA プレビューの描画バックエンド選択（ライト版 = three.js / リッチ版 = Unity WebGL）の
 * 永続化ユーティリティ。
 *
 * theme.ts と同じ localStorage + pub-sub の形を踏襲しているが、以下の点が異なる:
 * - 'system'（OS 追随）に相当する概念が無い。値は 'light' / 'rich' の2値のみなので、
 *   theme.ts の ensureMediaListener（matchMedia 追随）に相当する仕組みは不要。
 * - <html> に属性を書かない（表示テーマのように CSS 全体へ影響するものではないため）。
 *   よって index.html の FOUC 対策インラインスクリプトのような複製も不要。
 * - 「この環境で Unity WebGL が実際に使えるか」という実行時の可用性はここに持たない。
 *   永続設定（ユーザーの選択）と実行時の可用性（端末・ネットワーク依存で毎回変わりうる）を
 *   混ぜると、失敗時に設定そのものを書き換えてしまいがちになる。可用性はセッション限りの
 *   状態として unityViewer.ts（markUnityUnavailable / getUnityUnavailableReason）に分離する。
 */

export type PreviewQuality = 'light' | 'rich';

const STORAGE_KEY = 'vrma-hub:preview-quality';

// 既定は light。リッチ版は数MB〜10MB のダウンロードと数秒の初期化を伴うため、
// ユーザーが明示的に選ぶまで有効にしない。これでモバイルの既定も自然に light になり、
// 端末スペックによる機械的なブロック（誤爆しやすい）が不要になる。
const DEFAULT_QUALITY: PreviewQuality = 'light';

function isPreviewQuality(value: string | null): value is PreviewQuality {
  return value === 'light' || value === 'rich';
}

/** localStorage に保存されているユーザーの明示的な選択（無ければ null）。 */
export function getStoredPreviewQuality(): PreviewQuality | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isPreviewQuality(stored) ? stored : null;
}

/** 現在有効な設定（未選択なら既定値）。 */
export function getPreviewQuality(): PreviewQuality {
  return getStoredPreviewQuality() ?? DEFAULT_QUALITY;
}

/** 設定を保存し、購読者へ通知する。 */
export function setPreviewQuality(q: PreviewQuality): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, q);
  notify();
}

/** 現在の設定を反転させて確定保存する。 */
export function togglePreviewQuality(): PreviewQuality {
  const next: PreviewQuality = getPreviewQuality() === 'rich' ? 'light' : 'rich';
  setPreviewQuality(next);
  return next;
}

type Listener = (quality: PreviewQuality) => void;
const listeners = new Set<Listener>();

function notify(): void {
  const quality = getPreviewQuality();
  listeners.forEach((fn) => fn(quality));
}

/** 設定の変化を購読する。戻り値の関数を呼ぶと購読解除。 */
export function subscribePreviewQuality(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
